import React from 'react';
import confirm from 'antd/lib/modal/confirm';

import { clone } from 'common/utils/generic';
import { Section, Genre } from 'common/constants/types';
import { isEquivalentAttribute, parseSectionToAttributeNameAndValues } from 'pages/UserPage/components/MiddlePane/utils/displayAttributes';
import { simplePut, simpleDelete, simpleFetch } from 'common/utils/http';
import { formatAsFullDate } from 'common/utils/time';
import {
  AttributeEditType,
  AddedAttribute,
  AttributeEdit,
  AttributeDateChange,
  AddedAttributeType,
} from '../types';

export const addNewUserAttributeType = (
  name: string,
  singleton: boolean,
  attributeValue: string | number | undefined,
  userAttributeTypes: AddedAttributeType[]
): AddedAttributeType[] => {
  const updatedUserAttributeTypes = clone(userAttributeTypes);
  updatedUserAttributeTypes.push({
    name,
    singleton,
    attributes: attributeValue ? [{ value: attributeValue, is_derived: false }] : [],
  });
  return updatedUserAttributeTypes;
};

// only use this function for singletons
export const updateUserAttributeTypeValue = (
  attributeTypeName: string,
  attributeValue: string | number,
  userAttributeTypes: AddedAttributeType[]
): AddedAttributeType[] => {
  const updatedUserAttributeTypes = clone(userAttributeTypes);
  const foundInd = updatedUserAttributeTypes.findIndex(
    attributeType => attributeType.name === attributeTypeName
  );
  const foundAttributeType = updatedUserAttributeTypes[foundInd];
  if (!foundAttributeType.singleton) {
    throw new Error('Tried changing value of a non-singleton');
  }
  if (foundInd !== -1) {
    foundAttributeType.attributes = [{ value: attributeValue }];
  }
  return updatedUserAttributeTypes;
};

// do not use this function for singletons
export const addUserAttributeTypeValue = (
  attributeTypeName: string,
  attributeValue: string | number,
  userAttributeTypes: AddedAttributeType[]
): AddedAttributeType[] => {
  const updatedUserAttributeTypes = clone(userAttributeTypes);
  const foundInd = updatedUserAttributeTypes.findIndex(
    attributeType => attributeType.name === attributeTypeName
  );
  const foundAttributeType = updatedUserAttributeTypes[foundInd];
  if (foundAttributeType?.singleton) {
    throw new Error('Tried adding values to a singleton');
  }
  if (
    foundAttributeType &&
        !foundAttributeType.attributes.find(attribute => attribute.value === attributeValue)
  ) {
    updatedUserAttributeTypes[foundInd].attributes.push({ value: attributeValue });
  }
  return updatedUserAttributeTypes;
};

export const addAttributeFromPicker = (
  pickedValues: [string, string, string, string | number | undefined],
  appData: { genres: Genre[] },
  userAttributeTypes: AddedAttributeType[],
  addedSections: Section[],
  setUserAttributeTypes: (userAttrTypes: AddedAttributeType[]) => void,
  setAddedSections: (addedAttributes: Section[]) => void
) => {
  const [genreName, sectName, attrTypeName, attrVal] = pickedValues;
  const newAddedSections = clone(addedSections);
  // Search for the selected genre in the attributes data i.e. skills
  const selectedGenre = appData.genres.find(genre => genre.name === genreName);
  if (!selectedGenre) throw new Error(`could not find genre ${genreName} in API response`);

  // search for the correct section i.e. other_skills
  const selectedSection = selectedGenre.sections.find(v => v.name === sectName);
  if (!selectedSection) throw new Error(`could not find section ${sectName} in API response`);
  const selectedSectionClone = clone(selectedSection);

  // Filter only the correct attribute type i.e. construction_skills
  const selectedAttributeType = selectedSectionClone.attribute_types.find(
    p => p.name === attrTypeName
  );
  if (!selectedAttributeType)
    throw new Error(`could not find attribute type ${attrTypeName} in API response`);

  if (!selectedAttributeType.listable) {
    selectedAttributeType.attributes[0].value = ''; // Set default value to null
  }

  // Check if the section is already added in the added attribute
  const existingNewAddedSection = newAddedSections.find(section => section.name === sectName);

  if (existingNewAddedSection) {
    // Add the attribute if it's not in the list
    if (
      !existingNewAddedSection.attribute_types.find(
        attributeType => attributeType.name === attrTypeName
      )
    ) {
      existingNewAddedSection.attribute_types.push(selectedAttributeType);
    }
  } else {
    selectedSectionClone.attribute_types = [selectedAttributeType];
    newAddedSections.push(selectedSectionClone);
  }
  setAddedSections(newAddedSections);

  // Check if the attribute type already exists
  const currentUserAttributeType = userAttributeTypes.find(
    attributeType => attributeType.name === attrTypeName
  );
  if (!currentUserAttributeType) {
    const updatedUserAttributeTypes = addNewUserAttributeType(
      attrTypeName,
      selectedAttributeType.singleton,
      attrVal,
      userAttributeTypes
    );
    setUserAttributeTypes(updatedUserAttributeTypes);
  } else if (attrVal && currentUserAttributeType.singleton) {
    const updatedUserAttributeTypes = updateUserAttributeTypeValue(
      attrTypeName,
      attrVal,
      userAttributeTypes
    );
    setUserAttributeTypes(updatedUserAttributeTypes);
  } else if (attrVal) {
    const updatedUserAttributeTypes = addUserAttributeTypeValue(
      attrTypeName,
      attrVal,
      userAttributeTypes
    );
    setUserAttributeTypes(updatedUserAttributeTypes);
  }

  // setTimeout is to add scrolling to the added element AFTER the element actually gets
  // added on the event loop. Without setTimeout document.getElementById(attrTypeName)
  // returns undefined.
  setTimeout(() => {
    const addedAttributeDOMElement = document.getElementById(attrTypeName);
    if (addedAttributeDOMElement) {
      addedAttributeDOMElement.scrollIntoView({ behavior: 'smooth' });
    }
  });
};

export const AreSameStartEndDate = (
  attribute1: AddedAttribute,
  attribute2: AddedAttribute
): boolean =>
  attribute1.value === attribute2.value &&
    attribute1.id === attribute2.id &&
    attribute1.start_date === attribute2.start_date &&
    attribute1.end_date === attribute2.end_date;

export const AreSameAttribute = (attribute1: AddedAttribute, attribute2: AddedAttribute): boolean =>
  attribute1.value === attribute2.value &&
    attribute1.id === attribute2.id &&
    attribute1.start_date === attribute2.start_date &&
    attribute1.end_date === attribute2.end_date &&
    attribute1.is_derived === attribute2.is_derived &&
    attribute1.is_secondary === attribute2.is_secondary;

export const parseAttributeEdits = (
  originalSections: Section[],
  userAttributeTypes: AddedAttributeType[]
): {
    attributeEdits: AttributeEdit[];
    attributeDateChanges: AttributeDateChange[];
} => {
  const attributeEdits: AttributeEdit[] = [];
  const attributeDateChanges: AttributeDateChange[] = [];
  const origUserAttributeTypes = parseSectionToAttributeNameAndValues(originalSections);

  userAttributeTypes.forEach(userAttributeType => {
    const newAttributes: AddedAttribute[] = [];
    const origAttributeType = origUserAttributeTypes.find(
      attrType => attrType.name === userAttributeType.name
    );
    if (origAttributeType) {
      const origAttributeTypeClone = clone(origAttributeType);
      const origAttributes = origAttributeTypeClone.attributes.filter(
        attribute => !attribute.is_derived
      );

      userAttributeType.attributes
        .filter(attribute => !attribute.is_derived)
        .forEach(attribute => {
          const changedAttribute = origAttributes.find(
            at => isEquivalentAttribute(at, attribute) && at.id === attribute.id
          );
          if (!changedAttribute) {
            newAttributes.push(attribute);
          } else if (attribute.id && !AreSameStartEndDate(changedAttribute, attribute)) {
            const startDateChange =
                            !!attribute.start_date &&
                            changedAttribute.start_date !== attribute.start_date;
            const endDateChange =
                            !!attribute.end_date &&
                            changedAttribute.end_date !== attribute.end_date;
            attributeDateChanges.push({
              attributeId: attribute.id,
              name: origAttributeType.name,
              value: attribute.value,
              newStartDate: startDateChange ? attribute.start_date : undefined,
              newEndDate: endDateChange ? attribute.end_date : undefined,
            });
          } else if (attribute.id && !AreSameAttribute(changedAttribute, attribute)) {
            attributeEdits.push({
              put: true,
              name: userAttributeType.name,
              attribute,
            });
          }
        });

      const userAttributeTypeValues = userAttributeType.attributes
        .filter(attribute => !attribute.is_derived)
        .map(attribute => attribute.value);
      // attributes in userAttributeTypeValues and in currentAttributeTypeValues
      const intersectionAttributeValues = userAttributeTypeValues.filter(value =>
        userAttributeTypeValues.includes(value)
      );

      const deletedAttributes = origAttributeTypeClone
        ? origAttributeTypeClone.attributes.filter(
          attribute =>
            !intersectionAttributeValues.includes(attribute.value) &&
                          !attribute.is_derived
        )
        : [];
      deletedAttributes.forEach(deletedAttribute =>
        attributeEdits.push({
          put: false,
          name: userAttributeType.name,
          attribute: {
            value: deletedAttribute.value,
            is_secondary: deletedAttribute.is_secondary,
          },
        })
      );
    } else {
      // if there is no current attribute type then add all the non-derived attributes
      userAttributeType.attributes
        .filter(attribute => !attribute.is_derived)
        .forEach(attribute => newAttributes.push(attribute));
    }

    // add an attribute edit for each new attribute (unless it doesn't have a value)
    newAttributes.forEach(newAttribute => {
      if (newAttribute.value) {
        attributeEdits.push({
          put: true,
          name: userAttributeType.name,
          attribute: newAttribute,
        });
      }
    });
  });

  return { attributeEdits, attributeDateChanges };
};

// Sorting attributes by not adds (DELETEs) first, adds (PUTs) second
export const attributeAddComparator = (a: AttributeEdit, b: AttributeEdit) => {
  // e.g. if a is not an add and b is an add, return -1 (b > a)
  // if a is an add and b is not an add, return 1 (a > b)
  if (!a.put && b.put) return -1;
  if (a.put && !b.put) return 1;
  return 0; // a.add == b.add, don't change order
};

export const serialize = (
  name: string,
  attributes: AddedAttribute[] | string | number,
  isSecondary?: boolean
): string[] => {
  if (attributes instanceof Array) {
    return attributes.flatMap(attribute =>
      serialize(name, attribute.value, attribute.is_secondary)
    );
  }

  if (isSecondary) {
    return [`${encodeURIComponent(name)}=is_secondary:${encodeURIComponent(attributes)}`];
  }
  return [`${encodeURIComponent(name)}=${encodeURIComponent(attributes)}`];
};

export const makeChangeAttributeUrl = (
  userId: number,
  attributeEdit: AttributeEdit,
  isDryRun: boolean
) => {
  const { attribute } = attributeEdit;
  const params = serialize(
    attributeEdit.name,
    attribute.value,
    attributeEdit.attribute.is_secondary
  );
  if (isDryRun) {
    params.push('dry_run=true');
  }

  return `/api/v1/people/${userId}/attributes?${params.join('&')}`;
};

export const makeEditAttributeDateUrl = (attributeDateChange: AttributeDateChange) => {
  const params = [];
  if (attributeDateChange.newStartDate) {
    params.push(`start-date=${attributeDateChange.newStartDate}`);
  }
  if (attributeDateChange.newEndDate) {
    params.push(`end-date=${attributeDateChange.newEndDate}`);
  }
  return `/api/v1/attribute/${attributeDateChange.attributeId}/dates?${params.join('&')}`;
};

export const onConfirmUpdateAttributes = async (
  selectedUsers: number[],
  attributeEdits: AttributeEdit[],
  attributeDateChanges: AttributeDateChange[],
  setUserData: (user: unknown) => void
) => {
  // sorting the attributes with deletes first, puts last
  const sortedAttributeEdits = attributeEdits.sort(attributeAddComparator);
  /**
     * Creates a list that has a promise chain per user
     * [Promise.resolve().then(() => user 1 DELETE).then(() => user 1 PUT).then(...),
     *  Promise.resolve().then(() => user 2 DELETE).then(() => user 2 PUT).then(...), ... ]
     *
     * NOTE: No try/catch because we assume dry run would've caused/caught all errors
     */
  try {
    const userPromiseChains = selectedUsers.map(userId =>
      sortedAttributeEdits.reduce(
        (promiseChain, attributeEdit) =>
        // promise chaining our requests serially, per user
          promiseChain.then(() => {
            const fetchFunction = attributeEdit.put ? simplePut : simpleDelete;
            // NOT DRY RUN! e.g. fetchFunction(..., false)
            return Promise.resolve(
              fetchFunction(makeChangeAttributeUrl(userId, attributeEdit, false))
            );
          }),
        Promise.resolve()
      )
    );

    const dateChangeRequests = attributeDateChanges.map(async dateChange =>
      simplePut(makeEditAttributeDateUrl(dateChange))
    );

    // resolve all promises before moving forward
    await Promise.all([...userPromiseChains, ...dateChangeRequests]);
  } catch (e) {
    // TODO: how do we surface this to a user?
    console.error('Error: Non-dry run attempt to modify user(s) failed with: ', e);
  }

  if (selectedUsers.length === 1) {
    const user = await simpleFetch(`/api/v1/people/${selectedUsers[0]}`);
    setUserData(user);
  } else if (selectedUsers.length > 1) {
    const formattedQuery = `${selectedUsers
      .slice(1, selectedUsers.length)
      .map(id => `intersectionWith=${id}`)
      .join('&')}`;
    try {
      const res = await simpleFetch(`api/v1/people/${selectedUsers[0]}?${formattedQuery}`);
      setUserData(res);
    } catch (e) {
      console.error(
        'MiddlePane - While fetching user:',
        JSON.stringify(selectedUsers),
        ', received error:',
        e
      );
    }
  }
  window.location.reload();
};

interface Mutation {
    type: AttributeEditType;
    attribute_type: string;
    attribute_value: string;
    person: string;
}
interface DryRunResult {
    mutations: Mutation[];
}
const confirmModalContent = (
  dryRunResults: DryRunResult[],
  attributeDateChanges: AttributeDateChange[]
) => {
  const flatResults = dryRunResults
    .filter(result => !!result.mutations)
    .flatMap(result => result.mutations);
  const getResultString = (
    type: AttributeEditType,
    attrType: string,
    attrVal: string,
    person: string
  ) => {
    switch (type) {
    case 'add':
      return `- add ${attrType}: ${attrVal} to ${person}`;
    case 'add_derived':
      return `- add derived ${attrType}: ${attrVal} to ${person}`;
    case 'remove':
      return `- remove ${attrType}: ${attrVal} from ${person}`;
    case 'remove_derived':
      return `- remove derived ${attrType}: ${attrVal} from ${person}`;
    default:
      return '';
    }
  };
  const getDateChangeString = (attributeDateChange: AttributeDateChange) => {
    let resultString = '- change';
    if (attributeDateChange.newStartDate) {
      resultString += ` start date of "${attributeDateChange.name}" ${
        attributeDateChange.value
      } to ${formatAsFullDate(attributeDateChange.newStartDate)}`;
      if (attributeDateChange.newEndDate) {
        resultString += ` and end date to ${formatAsFullDate(attributeDateChange.newEndDate)}`;
      }
    } else if (attributeDateChange.newEndDate) {
      resultString += ` end date of "${attributeDateChange.name}" ${
        attributeDateChange.value
      } to ${formatAsFullDate(attributeDateChange.newEndDate)}`;
    }
    return resultString;
  };
  const formattedResults = flatResults.map(({ attribute_type, attribute_value, person, type }) =>
    getResultString(type, attribute_type, attribute_value, person)
  );
  const uniqueResults = [
    ...new Set(formattedResults),
    ...new Set(attributeDateChanges.map(dateChange => getDateChangeString(dateChange))),
  ];
  return uniqueResults.map(result => <div key={result}>{result}</div>);
};

export const renderConfirmUpdateModal = (
  dryRunResults: DryRunResult[],
  attributeDateChanges: AttributeDateChange[],
  onConfirm: () => void
) => {
  confirm({
    title: 'Are you sure you want to make the following changes?',
    content: confirmModalContent(dryRunResults, attributeDateChanges),
    onOk: onConfirm,
    okText: 'Save',
    cancelText: 'Cancel',
    okCancel: true,
    width: 400,
  });
};
